import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedEventImage } from "../utils/imageUtils";
import { useEventImage } from "../utils/useEventImage";
import { RunnerDetailsModal } from "../components/RunnerDetailsModal";
import { useEventId } from "../EventContext";
import { InputField, Button } from "@vionixasia/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import { resolveInitialCategory, writeLastCategory } from "../utils/categoryPreference";
import { toDate } from "../utils/firestoreTime";

// Capitalize the first letter of each word
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Convert nationality to ISO flag code
// Format gender with icon
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return <><FontAwesomeIcon icon={faMars} className="mr-1" />Male</>;
  if (normalizedGender === "female" || normalizedGender === "f")
    return <><FontAwesomeIcon icon={faVenus} className="mr-1" />Female</>;
  return "-";
};

const cleanFinishSegment = (value) => {
  if (value == null) return "";
  let str = String(value).trim();
  if (!str) return "";
  const cotMatch = str.match(/^COT\s*\((.*)\)$/i);
  if (cotMatch) {
    const inner = cleanFinishSegment(cotMatch[1]);
    return inner ? `COT (${inner})` : "COT (-)";
  }
  if (str.includes("/")) {
    const parts = str.split("/");
    str = parts[parts.length - 1].trim();
  }
  return str.replace(/\.\d+$/, "");
};

const getFinishDisplay = (...candidates) => {
  for (const candidate of candidates) {
    const cleaned = cleanFinishSegment(candidate);
    if (cleaned) return cleaned;
  }
  return "-";
};

// Get hero section details
const getHeroDetails = (selectedCategory, categories, eventName, userTimeZone) => {
  const now = moment().tz(userTimeZone);
  if (!selectedCategory) {
    return { title: eventName, dateTime: "Multiple Dates", startTime: null, cutOffTime: null, isCOT: false };
  }
  const category = categories.find((cat) => cat.id === selectedCategory);
  if (!category || !category.startTime) {
    return { title: "Unknown Category", dateTime: "Unknown Date", startTime: null, cutOffTime: null, isCOT: false };
  }
  const startTime = moment(toDate(category.startTime)).tz(userTimeZone);
  const cutOffTime = category.cutOffTime ? moment(toDate(category.cutOffTime)).tz(userTimeZone) : null;
  const isCOT = cutOffTime?.isValid() && now.isAfter(cutOffTime);
  return {
    title: category.name?.trim() || "Unknown Category",
    dateTime: startTime.isValid() ? startTime.format("DD MMM YYYY [at] HH:mm") : "Unknown Date",
    startTime: category.startTime,
    cutOffTime: category.cutOffTime,
    isCOT,
  };
};

// Stopwatch Component
const Stopwatch = ({ startTime, cutOffTime, eventStatus, categoryName, isCOT }) => {
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [isCOTState, setIsCOTState] = useState(isCOT || false);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  useEffect(() => {
    if (!startTime || eventStatus !== "ongoing") {
      setIsCOTState(isCOT || false);
      return;
    }

    const start = moment(toDate(startTime)).tz(userTimeZone);
    const cutOff = cutOffTime ? moment(toDate(cutOffTime)).tz(userTimeZone) : null;

    if (!start.isValid()) return;

    const updateTime = () => {
      const now = moment().tz(userTimeZone);
      const duration = moment.duration(now.diff(start));
      const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");
      setElapsedTime(`${hours}:${minutes}:${seconds}`);
      if (cutOff?.isValid() && now.isAfter(cutOff)) {
        setIsCOTState(true);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [startTime, cutOffTime, eventStatus, isCOT, userTimeZone]);

  if (!startTime || eventStatus !== "ongoing") {
    if (isCOTState) {
      return (
        <div className="absolute bottom-2 left-2 md:bottom-4 md:right-4 md:left-auto flex flex-row items-center gap-2 z-10">
          <span className="text-md md:text-lg text-center text-white drop-shadow-md">
            FINISHED {categoryName || "All Categories"}
          </span>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="absolute bottom-2 left-2 md:bottom-4 md:right-4 md:left-auto flex flex-row items-center gap-2 z-10">
      <span className="text-md md:text-lg text-center text-white drop-shadow-md">
        {isCOTState
          ? `FINISHED ${categoryName || "Unknown Category"}`
          : `ONGOING ${categoryName || "Unknown Category"} ${elapsedTime}`}
      </span>
    </div>
  );
};

// Hero Component - same as Results.jsx
const Hero = ({ selectedCategory, categories, eventName, eventStatus, heroImage, location, eventId, slug }) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const { title, dateTime, startTime, cutOffTime, isCOT } = getHeroDetails(
    selectedCategory, categories, eventName, userTimeZone
  );
  const imageSrc = useEventImage(slug || eventId, heroImage);

  return (
    <div className="relative w-full h-[30vh] md:h-[60vh] rounded-card overflow-hidden">
      {imageSrc && (
        <img
          src={imageSrc}
          alt={eventName}
          className="absolute inset-0 w-full h-full object-cover z-0"
          fetchpriority="high"
          loading="eager"
        />
      )}
      <div className="absolute inset-0">
        <div className="hidden md:block md:absolute md:top-auto md:bottom-4 md:left-4 md:right-auto md:text-left z-10">
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-white drop-shadow-lg">
            {title}
          </h2>
          <p className="hidden md:block text-md md:text-xl md:mt-1 text-white drop-shadow-md">
            {location || "Indonesia"} • {dateTime}
          </p>
        </div>
        <Stopwatch
          startTime={startTime}
          cutOffTime={cutOffTime}
          eventStatus={eventStatus}
          categoryName={title}
          isCOT={isCOT}
        />
      </div>
    </div>
  );
};


// Top3Table Component
const Top3Table = ({ title, results, onNameClick, finishFor }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-card border border-slate-200 overflow-x-auto">
    <h2 className="text-lg font-semibold text-gray-900 mb-4 px-4 pt-4">
      {title}
    </h2>
    {/* table-fixed on mobile so the rank column holds the width declared below;
        table-auto would let it stretch and push the name further right again. */}
    <table className="min-w-full table-fixed md:table-auto border-collapse">
      <thead className="bg-slate-50 border-y border-slate-200">
        <tr>
          {/* Mobile geometry matches the overall result card, which insets its
              table by 8px (the card's own p-2) and then uses a 48px rank column.
              Reproduce that as 8px of left padding + w-12, so the name in both
              tables starts at the same 56px. Desktop keeps px-2 and 60px. */}
          <th className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Rank
          </th>
          <th className="px-0 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:max-w-[150px]">
            Name
          </th>
          {/* Pinned on mobile like the overall card's finish column, so table-fixed
              gives the remaining width to the name instead. pr-6 = that card's pr-4
              plus its 8px inset, so the times end on the same right edge too. */}
          <th className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Finish
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-slate-200">
        {results.length === 0 ? (
          <tr>
            <td colSpan={3} className="px-4 py-3 text-center text-sm text-gray-600">
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr key={`top3-${result.bib || index}`} className="hover:bg-slate-50 transition-colors">
              <td className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-sm text-gray-600 text-center">
                {result.genderRank > 0 ? (
                  <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
                    {result.genderRank}
                  </span>
                ) : "-"}
              </td>
              <td
                className="px-0 md:px-4 py-3 text-sm font-bold text-gray-900 uppercase max-w-0 md:max-w-[150px] truncate cursor-pointer hover:text-teal-600"
                onClick={() => onNameClick(result)}
              >
                {capitalizeWords(result.name) || "-"}
              </td>
              <td className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-sm font-semibold text-slate-600">
                {finishFor ? finishFor(result) : getFinishDisplay(result.finishDuration)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// Main Component
export const ResultsLoop = ({ initialEventData }) => {
  const { realEventId: eventId, slug } = useEventId();
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    const initial = {
      rankedResults: [],
      top3Male: [],
      top3Female: [],
      loading: true,
      selectedCategory: "",
      loopingCategories: [],
      eventName: "Event",
      eventStatus: "ongoing",
      heroImage: null,
      location: "Indonesia",
      enableCertificate: false,
      error: null,
    };
    try {
      const landingRaw = localStorage.getItem("cached_landing_events");
      if (landingRaw) {
        const events = JSON.parse(landingRaw);
        const match = events.find((e) => e.eventId === eventId || e.slug === eventId);
        if (match?.heroImage) initial.heroImage = match.heroImage;
      }
    } catch {}

    if (initialEventData) {
      const loopingCats = (initialEventData.categories || [])
        .filter((cat) => cat.isLooping)
        .map((cat) => ({
          id: cat.id,
          name: cat.name,
          isLooping: true,
          // Without this the page never knew a category was a relay: isRelay
          // read undefined, so the team columns (Members / Team / Runners)
          // silently rendered as the individual ones instead.
          isRelay: !!cat.isRelay,
          startTime: cat.startTime,
          cutOffTime: cat.cutOffTime,
          // Lap length is what turns lap counts into distance. This mapping
          // copies named fields rather than spreading the category, so a field
          // left out here is simply invisible to the page — which is why the
          // distance columns stayed hidden even with the data present.
          lapDistanceMeters: cat.lapDistanceMeters ?? null,
          // Marks a fixed-distance category, and the certificate needs it to
          // know a 50K runner covered 50km rather than trying to measure a lane
          // that was never set for them.
          targetLoops: cat.targetLoops ?? null,
          checkpoint: cat.checkpoints?.[0] || null,
        }));

      return {
        ...initial,
        eventName: initialEventData.eventName || initial.eventName,
        eventStatus: initialEventData.status || initial.eventStatus,
        heroImage: initialEventData.heroImage ?? initial.heroImage,
        location: initialEventData.location || initial.location,
        loopingCategories: loopingCats,
        // Reopen on the tab this operator last used rather than snapping back
        // to the first category on every refresh.
        selectedCategory: resolveInitialCategory(eventId, loopingCats),
        loading: false,
      };
    }

    return initial;
  });
  const [hasFetched, setHasFetched] = useState(() => Boolean(initialEventData));
  const [isFetchingResults, setIsFetchingResults] = useState(false);
  // Denominators for "rank N of M" on the certificate and runner modal.
  const [rankedTotals, setRankedTotals] = useState({ overall: 0, male: 0, female: 0, category: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [bibNumberInput, setBibNumberInput] = useState("");
  const debounceRef = useRef(null);
  const pollTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  // Pagination state — same contract as Results.jsx so all results pages behave
  // identically: fetch 100 at a time, append as the sentinel scrolls into view.
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotalPages, setResultsTotalPages] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const sentinelRef = useRef(null);
  // Read by the poll interval, which is created once per category and would
  // otherwise close over stale searchQuery / resultsPage values.
  const searchRef = useRef("");
  const pageRef = useRef(1);
  searchRef.current = searchQuery;
  pageRef.current = resultsPage;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  
  // Rows are ordered by overall rank, or by the shown gender's own placing when
  // the table is narrowed to one. Nothing else is sortable here, so there is no
  // sort state to carry.

  // The Gender header filters rather than sorts. A gender placing only
  // has meaning against the same gender, so ordering the mixed field by it
  // produced a table nobody could read a result from: the women's winner sat
  // at row 21, behind twenty men. Cycling "all → male → female" instead shows
  // one gender's podium at a time, counted 1, 2, 3 from the top.
  const [genderFilter, setGenderFilter] = useState(null); // null | "male" | "female"
  const cycleGenderFilter = useCallback(() => {
    setGenderFilter((current) =>
      current === null ? "male" : current === "male" ? "female" : null
    );
  }, []);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch event config
  useEffect(() => {
    if (initialEventData) {
      setHasFetched(true);
      return;
    }

    const fetchEventConfig = async () => {
      try {
        const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/timing/event_timing`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (response.ok) {
          const eventData = data.find((event) => event.eventId === eventId);
          if (eventData) {
            const loopingCats = (eventData.categories || [])
              .filter((cat) => cat.isLooping)
              .map((cat) => ({
                id: cat.id,
                name: cat.name,
                isLooping: true,
                isRelay: !!cat.isRelay,
                startTime: cat.startTime,
                cutOffTime: cat.cutOffTime,
                lapDistanceMeters: cat.lapDistanceMeters ?? null,
                targetLoops: cat.targetLoops ?? null,
                checkpoint: cat.checkpoints?.[0] || null,
              }));

            setState((prev) => ({
              ...prev,
              eventName: eventData.eventName || "Event",
              eventStatus: eventData.status || "ongoing",
              heroImage: eventData.heroImage || null,
              location: eventData.location || "Indonesia",
              loopingCategories: loopingCats,
              selectedCategory: resolveInitialCategory(eventId, loopingCats),
              loading: false,
            }));
            setHasFetched(true);
          }
        }
      } catch (error) {
        console.error("[ERROR] Fetching event config:", error);
        setState((prev) => ({ ...prev, error: error.message, loading: false }));
        setHasFetched(true);
      }
    };

    fetchEventConfig();
  }, [eventId, initialEventData]);

  // Fetch ranked results from API (server-side ranking)
  const fetchResults = useCallback(
    async ({ categoryId, search = "", page = 1, append = false } = {}) => {
      if (!eventId || !categoryId) return;
      if (!append) {
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        fetchAbortRef.current = new AbortController();
      }
      const signal = !append ? fetchAbortRef.current.signal : undefined;
      setIsFetchingResults(true);
      try {
        const params = new URLSearchParams({
          eventId,
          category: categoryId,
          type: "loop",
          page: String(page),
          limit: "100",
          ...(search ? { search } : {}),
        });
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/timing/results?${params}`,
          { signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setState((prev) => ({
          ...prev,
          rankedResults: append
            ? [...prev.rankedResults, ...(data.results || [])]
            : (data.results || []),
          top3Male:  data.top3Male  || [],
          top3Female: data.top3Female || [],
          enableCertificate: data.enableCertificate ?? prev.enableCertificate ?? false,
          certificateBackground: data.certificateBackground ?? prev.certificateBackground ?? "",
          certificateLayout: data.certificateLayout ?? prev.certificateLayout ?? null,
          loading: false,
        }));
        setResultsPage(data.page || 1);
        setResultsTotalPages(data.totalPages || 1);
        setResultsTotal(data.total || 0);
        setRankedTotals({
          overall: data.rankedTotal ?? 0,
          male: data.rankedTotalMale ?? 0,
          female: data.rankedTotalFemale ?? 0,
          category: categoryId,
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("[ResultsLoop] fetchResults error:", e.message);
        setState((prev) => ({ ...prev, loading: false }));
      } finally {
        setIsFetchingResults(false);
      }
    },
    [eventId]
  );

  // Poll when category changes or on interval
  useEffect(() => {
    const validCat = state.selectedCategory && hasFetched;
    if (!validCat) return;
    fetchResults({ categoryId: state.selectedCategory, search: searchQuery });
    const interval = state.eventStatus === "ongoing" ? 10_000 : 60_000;
    pollTimerRef.current = setInterval(() => {
      // Never poll while the user is searching or has scrolled past page 1:
      // the refetch overwrote the search hit they had just typed (it appeared,
      // then vanished on the next tick) and discarded loaded pages.
      if (searchRef.current) return;
      if (pageRef.current > 1) return;
      fetchResults({ categoryId: state.selectedCategory, search: "" });
    }, interval);
    return () => clearInterval(pollTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCategory, state.eventStatus, hasFetched]);

  // Re-fetch on search change
  useEffect(() => {
    if (!state.selectedCategory || !hasFetched) return;
    fetchResults({ categoryId: state.selectedCategory, search: searchQuery });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ── Infinite scroll: sentinel enters viewport → append next page.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingResults) {
          // Derive the next page from a ref, not inside setResultsPage: fetching
          // in the updater makes it a side effect (React may run updaters twice,
          // double-loading a page) and the counter kept climbing past the last
          // page, so the sentinel stopped firing altogether.
          const next = pageRef.current + 1;
          if (next > resultsTotalPages) return;
          pageRef.current = next;
          setResultsPage(next);
          fetchResults({
            categoryId: state.selectedCategory,
            page: next,
            search: searchRef.current,
            append: true,
          });
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isFetchingResults, resultsTotalPages, fetchResults, state.selectedCategory, searchQuery]);

  // Get current category config
  const currentCategory = useMemo(() => {
    return state.loopingCategories.find((cat) => cat.id === state.selectedCategory);
  }, [state.loopingCategories, state.selectedCategory]);

  // Relay categories are scored as teams. Order IDs stay internal; public rows
  // show the member BIBs and runner names supplied by the API.
  const isRelay = !!currentCategory?.isRelay;

  // A lap length turns lap counts into distance. Set per category because the
  // same event can time different lanes — Indonesia Ultra ran 6H/12H at 423m
  // and 24H at 407.66m.
  const showsDistance =
    !isRelay && Number(currentCategory?.lapDistanceMeters) > 0;

  // A timed category has no per-runner finish time: everyone runs until the
  // cut-off, so the mats only record whenever each runner last crossed before
  // the horn. Published as-is, 23:58:11 beside 23:59:58 reads as one being
  // faster when both ran the full 24 hours — and the smaller number looks
  // better. Show the race duration instead; distance is what separates them.
  const timedFinishLabel = useMemo(() => {
    if (!currentCategory || Number(currentCategory.targetLoops) > 0) return null;
    // targetLoops alone is not enough to identify a timed race: Posrunners runs
    // fixed distances without one, and every runner there finishes at their own
    // lap 7 with a different time — 11:52 through 19:30. A lane length is the
    // reliable marker, because it is only ever set where distance is the
    // measure and the clock is shared.
    if (!(Number(currentCategory.lapDistanceMeters) > 0)) return null;
    const ms = (t) => {
      const v = t?.toDate ? t.toDate() : t;
      const n = v ? new Date(v).getTime() : NaN;
      return Number.isFinite(n) ? n : null;
    };
    const start = ms(currentCategory.startTime);
    const cut = ms(currentCategory.cutOffTime);
    if (start === null || cut === null || cut <= start) return null;
    const total = Math.floor((cut - start) / 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  }, [currentCategory]);

  // DNF/DNS/DSQ keep their own label — they did not run the duration.
  const finishFor = (result) => {
    // Past the cut-off is a result, not a time: the runner completed laps but
    // outside the window, so a clock reading would imply a placing they do not
    // hold. The lap count stays in its own column, which is what shows how far
    // they actually got.
    if (result.isCOT) return "OVER COT";
    if (["DNF", "DNS", "DSQ"].includes(result.status)) return result.status;
    if (timedFinishLabel && result.finishDuration) return timedFinishLabel;
    return result.finishDuration ? getFinishDisplay(result.finishDuration) : "-";
  };

  // Filter results (API already filters by category; local filter for instant UX)
  const filteredResults = useMemo(() => {
    let rows = state.rankedResults;
    if (genderFilter) {
      const wanted = genderFilter === "male" ? ["male", "m"] : ["female", "f"];
      rows = rows.filter((r) => wanted.includes(String(r.gender || "").toLowerCase()));
    }
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter(
      (r) =>
        r.bib?.toLowerCase().includes(query) ||
        r.name?.toLowerCase().includes(query) ||
        r.memberBibs?.some((bib) => String(bib).toLowerCase().includes(query)) ||
        r.members?.some((member) => member.name?.toLowerCase().includes(query))
    );
  }, [state.rankedResults, searchQuery, genderFilter]);

  // Sort results
  const sortedResults = useMemo(() => {
    return [...filteredResults].sort((a, b) => {
      // With one gender on screen, the meaningful order is that gender's own
      // placing, counted from 1 — so the women's winner heads the list rather
      // than sitting wherever her overall rank puts her.
      if (genderFilter) {
        const aRank = a.genderRank > 0 ? a.genderRank : Infinity;
        const bRank = b.genderRank > 0 ? b.genderRank : Infinity;
        if (aRank !== bRank) return aRank - bRank;
        return 0;
      }

      // Sorting by gender used to live here, reachable from a second clickable
      // header. One control does the job: narrowing to a gender already ranks it
      // from 1, which is what the sort was for.

      // Default sort by overall rank
      if (a.overallRank === -1 && b.overallRank !== -1) return 1;
      if (a.overallRank !== -1 && b.overallRank === -1) return -1;
      if (a.overallRank !== -1 && b.overallRank !== -1) return a.overallRank - b.overallRank;
      return 0;
    });
  }, [filteredResults, genderFilter]);

  const handleCategoryChange = useCallback((categoryId) => {
    setState((prev) => ({ ...prev, selectedCategory: categoryId }));
    writeLastCategory(eventId, categoryId);
    setSearchQuery("");
    setBibNumberInput("");
  }, [eventId]);

  const handleInputChange = useCallback((event) => {
    const input = event.target.value;
    setBibNumberInput(input);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(input), 500);
  }, []);

  const [selectedRunner, setSelectedRunner] = useState(null);

  const handleNameClick = useCallback(
    (runner) => { setSelectedRunner(runner); },
    []
  );

  if (hasFetched && state.loopingCategories.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <h2 className="text-xl font-semibold text-gray-700">No Looping Categories</h2>
        <p className="text-gray-500 mt-2">This event doesn't have any looping categories configured.</p>
        <a href={`/${slug}`} className="mt-4 text-gray-600 underline">View Standard Results →</a>
      </div>
    );
  }

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <Hero
          selectedCategory={state.selectedCategory}
          categories={state.loopingCategories}
          eventName={state.eventName}
          eventStatus={state.eventStatus}
          heroImage={state.heroImage}
          location={state.location}
          eventId={eventId}
          slug={slug}
        />

        {/* Category Buttons and Search */}
        <div className="mb-4 mt-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap gap-2">
            {state.loopingCategories.map((cat) => (
              <Button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                variant={state.selectedCategory === cat.id ? "primary" : "secondary"}
                color="slate"
              >
                {cat.name}
              </Button>
            ))}

          </div>
          <InputField
            type="text"
            value={bibNumberInput}
            onChange={handleInputChange}
            placeholder="Search BIB or Name"
            className="text-center shadow-sm"
            wrapperClassName="w-full md:w-64"
          />
        </div>

        {/* Relay is ranked per team and has no gender podium. */}
        {!isRelay && (
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table title="Top 3 Men" results={state.top3Male} onNameClick={handleNameClick} finishFor={finishFor} />
            <Top3Table title="Top 3 Women" results={state.top3Female} onNameClick={handleNameClick} finishFor={finishFor} />
          </div>
        )}

        {/* Main Table */}
        <div className="w-full">
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto pb-8">
            <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
              <thead>
                <tr>
                  <th className="bg-slate-50 px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[50px] rounded-l-card border-y border-l border-slate-200">
                    Rank
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] border-y border-slate-200">
                    Name
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    {isRelay ? "Members" : "Nationality"}
                  </th>
                  {/* Relay rows carry each runner's BIB next to their name in
                      the Runners column, so a separate Bib column repeats the
                      numbers and steals the width the names need. */}
                  {!isRelay && (
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Bib
                    </th>
                  )}
                  <th
                    className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors border-y border-slate-200"
                    onClick={isRelay ? undefined : cycleGenderFilter}
                    title={isRelay ? undefined : "Click: men only → women only → everyone"}
                  >
                    {isRelay ? "Runners" : "Gender"}
                    {/* One control, not two. Showing a gender also ranks it from
                        1, which is the only reason anyone narrows the table —
                        the women's winner should head the list, not sit at row
                        21 behind twenty men. */}
                    {!isRelay && genderFilter && (
                      <span className="ml-1">{genderFilter === "male" ? "♂" : "♀"}</span>
                    )}
                  </th>
                  {!isRelay && (
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Rank Gender
                    </th>
                  )}
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    LOOPS
                  </th>
                  {/* Distance only means something where a lap length is set:
                      a timed race ends on the clock, so what separates runners
                      is how far they got, not a finish time they all share. */}
                  {showsDistance && (
                    <>
                      <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                        EXTRA (M)
                      </th>
                      <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                        DISTANCE (KM)
                      </th>
                    </>
                  )}
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                    FINISH
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.length === 0 && !isFetchingResults ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-sm text-gray-600">
                      No race results available for this category.
                    </td>
                  </tr>
                ) : (
                  sortedResults.map((result, index) => {
                    const { code: flagCode, display: nationalityDisplay } = getFlagCode(result.nationality);

                    return (
                      <tr key={`${result.id || "unknown"}-${index}`} className="hover:-translate-y-[2px] transition-transform shadow-sm group">
                        <td className="bg-white px-2 py-4 text-sm text-slate-600 text-center w-[60px] rounded-l-card border-y border-l border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {result.overallRank > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
                              {result.overallRank}
                            </span>
                          ) : "-"}
                        </td>
                        <td
                          className="bg-white px-4 py-4 whitespace-nowrap text-sm font-bold text-slate-900 uppercase max-w-[150px] truncate cursor-pointer hover:text-teal-600 border-y border-slate-200 group-hover:bg-slate-50 transition-colors"
                          onClick={() => handleNameClick(result)}
                        >
                          {capitalizeWords(result.name) || "-"}
                        </td>
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {isRelay ? (
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {result.memberCount ?? (result.members?.length || 0)}
                            </span>
                          ) : (
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                            <img
                              src={`https://flagcdn.com/w20/${flagCode}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover"
                              alt=""
                            />
                            {nationalityDisplay || "-"}
                          </span>
                          )}
                        </td>
                        {!isRelay && (
                          <td className="bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {result.bib ? `BIB ${result.bib}` : "-"}
                            </span>
                          </td>
                        )}
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {/* Names on one line, wrapping only when the column
                              runs out: stacking one runner per row made the
                              relay rows five times taller than the rest, and a
                              table row is only as short as its tallest cell. */}
                          {isRelay ? (
                            <span className="text-xs leading-snug uppercase">
                              {result.members?.length
                                ? result.members
                                    .map((m) => `${m.bib} ${m.name}`)
                                    .join(" · ")
                                : "-"}
                            </span>
                          ) : (
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {formatGender(result.gender)}
                            </span>
                          )}
                        </td>
                        {!isRelay && (
                          <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                            <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                              {result.genderRank > 0 ? result.genderRank : "-"}
                            </span>
                          </td>
                        )}
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center whitespace-nowrap border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {["DNF", "DNS", "DSQ"].includes(result.status) ? (
                            <span className={result.status === "DNF" ? "text-orange-600 font-semibold" : result.status === "DSQ" ? "text-red-600 font-semibold" : "text-gray-500 font-semibold"}>{result.status}</span>
                          ) : (
                            <span className={`${result.isCOT ? "text-red-600" : "text-gray-700"} font-semibold inline-flex flex-col`}>
                              {result.completedLoops > 0
                                ? result.completedLoops
                                : "-"}
                            </span>
                          )}
                        </td>
                        {showsDistance && (
                          <>
                            <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center whitespace-nowrap border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                              {Number.isFinite(Number(result.extraMeters))
                                ? Number(result.extraMeters).toLocaleString(undefined, { maximumFractionDigits: 1 })
                                : "-"}
                            </td>
                            <td className="bg-white px-4 py-4 text-sm font-semibold text-slate-700 text-center whitespace-nowrap border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                              {Number.isFinite(Number(result.totalDistanceKm))
                                ? Number(result.totalDistanceKm).toFixed(2)
                                : "-"}
                            </td>
                          </>
                        )}
                        <td
                          className={`bg-white px-4 py-4 text-center text-sm font-semibold rounded-r-card border-y border-r border-slate-200 group-hover:bg-slate-50 transition-colors ${
                            result.status === "DNF" ? "text-orange-600" :
                            result.status === "DNS" ? "text-slate-500" :
                            result.status === "DSQ" ? "text-red-600" :
                            result.isCOT ? "text-red-600" :
                            "text-slate-600"
                          } whitespace-nowrap`}
                        >
                          {finishFor(result)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden space-y-3 pb-6">
            
            {/* Mobile Results */}
            {sortedResults.length === 0 && !isFetchingResults ? (
              <div className="px-4 py-6 text-center text-sm text-gray-600">
                No race results available for this category.
              </div>
            ) : (
              sortedResults.map((result, index) => {
                const { code: flagCode, display: nationalityDisplay } = getFlagCode(result.nationality);
                
                // Display logic: if finished show "Time / Duration", else show last completed loop time or status
                let displayLastLoop = "-";
                const lastLoopNum = Object.keys(result.loopTimestamps || {}).reduce((mx, k) => {
                        const n = /^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0;
                        return n > mx ? n : mx;
                    }, 0);
                const lastLoopKey = `loop${lastLoopNum}`;
                const lastLoopTime = result.loopTimestamps?.[lastLoopKey] || "-";

                if (["DNF", "DNS", "DSQ"].includes(result.status)) {
                    let statusClass = "text-gray-600";
                    if (result.status === "DNF") statusClass = "text-orange-600 font-semibold";
                    if (result.status === "DNS") statusClass = "text-gray-500 font-semibold";
                    if (result.status === "DSQ") statusClass = "text-red-600 font-semibold";
                    displayLastLoop = <span className={statusClass}>{result.status}</span>;
                } else if (result.finishDuration && result.finishDuration !== "-") {
                  const isCOT = result.isCOT;
                  const content = finishFor(result);
                  const className = isCOT ? "text-red-600 font-semibold" : "text-slate-700 font-medium";
                  displayLastLoop = <span className={className}>{content}</span>;
                } else {
                  // Not finished: show last completed loop time
                  displayLastLoop = lastLoopTime;
                }

                return (
                  <div
                    key={`mobile-${result.id || "unknown"}-${index}`}
                    className="bg-white rounded-card border border-slate-200 shadow-sm p-2 overflow-hidden"
                  >
                    {/* Row 1: Rank, Name, Last Loop */}
                    <table className="min-w-full table-fixed">
                      <tbody>
                        <tr>
                          {/* Same hierarchy as the standard results card: the
                              rank sits in a pill and the name is the boldest
                              thing on the row. Flat text at one weight left
                              nothing to read first. */}
                          <td className="w-12 py-2 text-sm font-medium text-slate-700 text-center">
                            {result.overallRank > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-100 text-slate-700 rounded-full font-bold text-xs">
                                {result.overallRank}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          {/* `truncate` needs something to truncate against. In
                              a table-fixed row with no width on this cell the
                              column just grew to fit the name, so a long one
                              pushed the finish time off the card instead of
                              being clipped the way the desktop table clips it. */}
                          <td
                            className="py-2 text-sm font-bold text-slate-900 uppercase truncate max-w-0 cursor-pointer"
                            onClick={() => handleNameClick(result)}
                          >
                            {capitalizeWords(result.name) || "-"}
                          </td>
                          <td className="w-24 py-2 text-sm font-medium text-right pr-4 text-slate-700">
                            {displayLastLoop}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    
                    {/* Row 2: Additional Info */}
                    <div className="flex items-center pb-2 gap-3 ml-12 border-t border-slate-200/50 pt-2 pr-4">
                      {isRelay ? (
                        <>
                          {/* Five BIBs wrapped onto extra lines and made the
                              relay cards taller than the rest; the range plus
                              the count says the same thing in one line. */}
                          <span className="text-xs text-slate-500 truncate min-w-0">
                            BIB {result.memberBibs?.length
                              ? `${result.memberBibs[0]}–${result.memberBibs[result.memberBibs.length - 1]}`
                              : (result.bib || "-")}
                          </span>
                          <span className="text-xs text-slate-500 shrink-0">{result.memberCount || 0} runners</span>
                        </>
                      ) : (<>
                      {/* Flag */}
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <img
                          src={`https://flagcdn.com/w20/${flagCode}.png`}
                          onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                          className="w-4 h-3 border border-slate-200 rounded object-cover"
                          alt=""
                        />
                        <span className="text-xs text-slate-500">
                          {nationalityDisplay || "-"}
                        </span>
                      </span>
                      {/* Bib */}
                      <span className="text-xs text-slate-500">
                        BIB {result.bib || "-"}
                      </span>
                      <span className="text-xs font-medium text-slate-600">
                        {result.completedLoops || 0} loop
                      </span>
                      {/* Gender Icon Only */}
                      <span className="text-xs text-slate-500">
                        <FontAwesomeIcon 
                          icon={result.gender?.toLowerCase() === 'male' || result.gender?.toLowerCase() === 'm' ? faMars : faVenus} 
                        />
                      </span>
                      {/* Spacer to push rank to right */}
                      <span className="flex-1"></span>
                      {/* Gender Rank - aligned right */}
                      <span className="text-xs text-slate-500 text-right whitespace-nowrap">
                        {/* The gender icon beside it already says which
                            ranking this is, so spelling it out spent most of a
                            phone-width row on the label instead of the number. */}
                        #<span className="font-medium text-slate-700">{result.genderRank > 0 ? result.genderRank : "-"}</span>
                      </span>
                      </>)}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Infinite scroll sentinel — matches Results.jsx so every results
              page loads in pages of 100 instead of one oversized request. */}
          <div ref={sentinelRef} className="pb-6 pt-2 min-h-[1px]">
            {resultsTotal > 0 && (
              resultsPage < resultsTotalPages ? (
                <div className="flex flex-col items-center gap-1 py-4">
                  {isFetchingResults && (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                  )}
                  <span className="text-xs text-slate-400">
                    Showing {filteredResults.length.toLocaleString()} of {resultsTotal.toLocaleString()}
                  </span>
                </div>
              ) : (
                <p className="text-center text-xs text-slate-400 py-2">
                  {resultsTotal.toLocaleString()} result{resultsTotal !== 1 ? "s" : ""}
                </p>
              )
            )}
          </div>
        </div>
      </div>

      {/* Teams open the modal too: it merges the members' laps, so a relay row
          is no longer the only row on the page that ignores a click. */}
      {selectedRunner && (
        <RunnerDetailsModal
          runner={selectedRunner}
          rankedTotals={rankedTotals}
          onClose={() => setSelectedRunner(null)}
          categories={state.loopingCategories}
          eventId={eventId}
          eventData={{
            enableCertificate: state.enableCertificate ?? false,
            eventName: state.eventName,
            eventStatus: state.eventStatus,
            // Without these the renderer silently falls back to the stock design.
            certificateBackground: state.certificateBackground || "",
            certificateLayout: state.certificateLayout || null,
          }}
        />
      )}
    </section>
  );
};
